import { AlertCircle, ArrowLeft, Calendar } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { SyncLoader } from 'react-spinners';
import { BRAND_LOADER_COLOR, PORTAL_ROUTES } from 'utils/constants';
import { PatientActions } from 'store/actions';
import { setAuthToken, setRefreshToken } from 'library/auth';
import toast from 'react-hot-toast';

// Generic portal landing — shown for both the bare /portal index and any
// session-expired redirect. Magic-link access only, so the message stays
// neutral: "use your link to come in".
const enhancer = connect(null, { ...PatientActions })

const Auth = ({ redirectToken, setPatient }) => {
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null)

  const authenticateRedirectToken = useCallback(async () => {
    if (!uid || !token) {
      setError('Invalid portal link. Please contact your dental office for a new one.');
      return;
    }

    const next = searchParams.get('next');
    const redirectTo = next?.startsWith('/portal/') ? next : PORTAL_ROUTES.DASHBOARD;

    try {
      const { auth_token, refresh_token, ...patient } = await redirectToken({ uid, token });

      setAuthToken(auth_token);
      setRefreshToken(refresh_token);
      setPatient(patient)

      setTimeout(() => {
        navigate(redirectTo, { replace: true });
      }, 200)
    } catch (error: any) {
      let { encoded_id } = error || {};
      if (encoded_id) {
        toast.error('Your portal link has expired. please verify yourself');
        navigate(`/portal/patients/${encoded_id}${next ? `?next=${redirectTo}` : ''}`, { replace: true });
        return
      }

      setError(error.errors || 'Unable to verify your portal link. Please contact your dental office for a new one.');
    }
  }, [navigate, redirectToken, searchParams, setPatient, token, uid]);

  useEffect(() => {
    authenticateRedirectToken();
  }, [authenticateRedirectToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-teamcare-blue/5 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div
            aria-hidden="true"
            className="h-1 bg-gradient-to-r from-teamcare-blue to-teamcare-cyan"
          />
          <div className="p-8 text-center">
            <div className="flex justify-center mb-5">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-teamcare-blue/25 rounded-2xl blur-xl"
                />
                <div className="relative w-16 h-16 bg-gradient-to-br from-teamcare-blue to-teamcare-blue-dark rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200/60">
                  <Calendar size={28} className="text-white" strokeWidth={2.25} />
                </div>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-secondary-900 mb-2">
              Patient Portal
            </h1>

            {error ? (
              <div
                role="alert"
                className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-left text-sm text-red-700"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle
                    size={18}
                    className="mt-0.5 flex-shrink-0 text-red-500"
                    strokeWidth={2.25}
                  />
                  <p>{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(PORTAL_ROUTES.ROOT)}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-inset ring-red-200 transition hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-red-50"
                >
                  <ArrowLeft size={16} strokeWidth={2.25} />
                  Go back to portal
                </button>
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-teamcare-blue/10 bg-teamcare-blue/5 px-5 py-4">
                <SyncLoader size={8} color={BRAND_LOADER_COLOR} />
                <p className="text-sm font-medium text-secondary-600">
                  Verifying your secure portal link...
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-secondary-400 mt-5">
          Trouble with the link? Contact your dental office for a new one.
        </p>
      </div>
    </div>
  )
};

export default enhancer(Auth)
